import React, { useState, useEffect } from 'react';
import { X, Save, Eye, EyeOff, Search, UserPlus, Users } from 'lucide-react';
import { User } from 'types';
import { ROLES, USER_TYPES } from 'utils/constants';
import { useAuth } from 'hooks/useAuth';
import { organizationsService, Organization } from 'api/services/organizations.service';
import { usersService } from 'api/services/users.service';

interface UserModalProps {
  user: User | null;
  onClose: () => void;
  onSave: (data: Omit<User, 'id'>) => void;
}

type ModalMode = 'create' | 'assign';

const UserModal: React.FC<UserModalProps> = ({ user, onClose, onSave }) => {
  const { user: currentUser } = useAuth();

  const [mode, setMode] = useState<ModalMode>('create');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchedUser, setSearchedUser] = useState<User | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [formData, setFormData] = useState<Omit<User, 'id'>>({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    organizations: [],
    role: 'operator_user',
    user_type: 'business'
  });

  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [businesses, setBusinesses] = useState<Organization[]>([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');

  // Update role when organization selection changes in assign mode
  useEffect(() => {
    if (mode === 'assign' && selectedOrganizationId) {
      const selectedOrg = businesses.find(b => b.id === selectedOrganizationId);
      if (selectedOrg) {
        // Set default role based on organization type
        // For business orgs, default to service_admin (since service provider staff typically manage businesses)
        // For service orgs, default to service_user
        if (selectedOrg.type === 'BUSINESS') {
          setFormData(prev => ({ ...prev, role: 'service_admin' }));
        } else if (selectedOrg.type === 'SERVICE') {
          setFormData(prev => ({ ...prev, role: 'service_user' }));
        }
      }
    }
  }, [selectedOrganizationId, mode, businesses]);

  useEffect(() => {
    if (user) {
      const { id, ...userData } = user;
      setFormData({ ...userData, password: '' });
    } else {
      // Set default values based on current user's role
      const currentRole = currentUser?.organizations?.[0]?.role || currentUser?.role;
      if (currentRole === 'operator_admin' && currentUser) {
        setFormData({
          username: '',
          password: '',
          firstName: '',
          lastName: '',
          email: '',
          organizations: [],
          role: 'operator_user',
          user_type: 'business',
          organization_id: currentUser.organizations?.[0]?.id
        });
      } else if (currentRole === 'service_admin') {
        setFormData({
          username: '',
          password: '',
          firstName: '',
          lastName: '',
          email: '',
          organizations: [],
          role: 'service_user',
          user_type: 'service'
        });
      } else if (currentRole === 'super_admin' || currentRole === 'master_admin') {
        setFormData({
          username: '',
          password: '',
          firstName: '',
          lastName: '',
          email: '',
          organizations: [],
          role: 'service_user',
          user_type: 'service'
        });
      }
    }
  }, [user, currentUser]);

  // Load organizations for master_admin, super_admin and service_admin
  useEffect(() => {
    const currentRole = (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase();

    if (currentRole === 'master_admin' || currentRole === 'super_admin' || currentRole === 'service_admin') {
      const loadOrganizations = async () => {
        try {
          setLoadingBusinesses(true);
          let allOrgs: Organization[] = [];

          if (currentRole === 'master_admin' || currentRole === 'super_admin') {
            // Load all organizations (both service and business)
            const [businessResponse, serviceResponse] = await Promise.all([
              organizationsService.getAllBusinesses(),
              organizationsService.getServiceProviders()
            ]);

            if (businessResponse?.success && businessResponse.data) {
              allOrgs = [...allOrgs, ...businessResponse.data];
            }
            if (serviceResponse?.success && serviceResponse.data) {
              allOrgs = [...allOrgs, ...serviceResponse.data];
            }
          } else {
            // service_admin - get businesses under their service provider
            const serviceProviderId = currentUser?.organizations?.[0]?.id;
            if (serviceProviderId) {
              const response = await organizationsService.getBusinessesByServiceProvider(serviceProviderId);
              if (response?.success && response.data) {
                allOrgs = response.data;
              }
            }
          }

          setBusinesses(allOrgs);
        } catch (error) {
          console.error('Error loading organizations:', error);
        } finally {
          setLoadingBusinesses(false);
        }
      };

      loadOrganizations();
    }
  }, [currentUser]);

  const validate = () => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};

    // In assign mode, user must be searched first
    if (mode === 'assign' && !searchedUser) {
      setSearchError('Please search for a user first');
      return false;
    }

    // In assign mode, organization must be selected
    if (mode === 'assign' && !selectedOrganizationId) {
      newErrors.organization_id = 'Organization is required';
      return false;
    }

    if (!formData.username) {
      newErrors.username = 'Username is required';
    }
    // Only validate password in create mode
    if (mode === 'create' && !user && !formData.password) {
      newErrors.password = 'Password is required for new users';
    }
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (mode === 'create' && formData.user_type?.toLowerCase() === 'business' && !formData.organization_id) {
      newErrors.organization_id = 'Organization is required for business users';
    }
    if (mode === 'create' && formData.user_type?.toLowerCase() === 'service' &&
        (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'super_admin' &&
        !formData.organization_id) {
      newErrors.organization_id = 'Organization is required for service users';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      const dataToSave: any = { ...formData };
      console.log(dataToSave);
      // In assign mode, use the selectedOrganizationId
      if (mode === 'assign' && selectedOrganizationId && formData.role) {
        // Backend expects organizations array with just id and role
        dataToSave.organizations = [{
          id: selectedOrganizationId,
          role: formData.role
        }];
      }

      if (user && !formData.password) {
        delete dataToSave.password;
      }
      onSave(dataToSave);
    }
  };

  const handleSearchUser = async () => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter a username or email');
      return;
    }

    setSearching(true);
    setSearchError('');
    setSearchedUser(null);

    try {
      const response = await usersService.searchUser(searchQuery.trim());

      if (response.success && response.data) {
        setSearchedUser(response.data);
        // Populate form with found user data
        setFormData({
          username: response.data.username || '',
          password: '', // No password for existing user
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          email: response.data.email || '',
          organizations: response.data.organizations || [],
          role: 'operator_user', // Default, will be changed by admin
          user_type: 'business'
        });
      } else {
        setSearchError(response.error || 'User not found');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setSearchError('Failed to search user. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleModeChange = (newMode: ModalMode) => {
    setMode(newMode);
    setSearchQuery('');
    setSearchedUser(null);
    setSearchError('');

    // For assign mode, pre-select organization based on current user's role
    if (newMode === 'assign') {
      const currentRole = (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase();
      if (currentRole === 'operator_admin') {
        // Pre-select their own organization (operator_admin can only assign to their business)
        const orgId = currentUser?.organizations?.[0]?.id;
        if (orgId) {
          setSelectedOrganizationId(orgId);
        }
      } else {
        // For super_admin and service_admin, let them choose from dropdown
        setSelectedOrganizationId('');
      }

      setFormData({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        organizations: [],
        role: 'operator_user',
        user_type: 'business'
      });
    }

    // Reset form when switching modes
    if (newMode === 'create') {
      setSelectedOrganizationId('');
      const currentRole = currentUser?.organizations?.[0]?.role || currentUser?.role;
      if (currentRole === 'operator_admin' && currentUser) {
        setFormData({
          username: '',
          password: '',
          firstName: '',
          lastName: '',
          email: '',
          organizations: [],
          role: 'operator_user',
          user_type: 'business',
          organization_id: currentUser.organizations?.[0]?.id
        });
      } else {
        setFormData({
          username: '',
          password: '',
          firstName: '',
          lastName: '',
          email: '',
          organizations: [],
          role: 'service_user',
          user_type: 'service'
        });
      }
    }
  };

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      const currentRole = currentUser?.organizations?.[0]?.role || currentUser?.role;

      // When user_type changes, update default role
      if (field === 'user_type') {
        if (currentRole?.toLowerCase() === 'service_admin') {
          // Service admin: set appropriate default role based on user type
          if (value?.toLowerCase() === 'business') {
            updated.role = 'operator_user'; // Default to operator_user for business users
          } else {
            updated.role = 'service_user'; // Default to service_user for service users
          }
        } else if (currentRole?.toLowerCase() === 'operator_admin' && currentUser) {
          if (value?.toLowerCase() === 'business') {
            updated.organization_id = currentUser.organizations?.[0]?.id;
          } else {
            delete updated.organization_id;
          }
        }

        // Clear organization_id when switching to service user (unless super_admin/master_admin will select it)
        if (value?.toLowerCase() === 'service') {
          // Only delete organization_id if current role is not super_admin/master_admin (they will select it)
          if (currentRole?.toLowerCase() !== 'super_admin' && currentRole?.toLowerCase() !== 'master_admin') {
            delete updated.organization_id;
          }
        }
      }

      return updated;
    });
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Determine available user types based on current user's role
  const getAvailableUserTypes = () => {
    const currentRole = currentUser?.organizations?.[0]?.role || currentUser?.role;
    if (currentRole?.toLowerCase() === 'master_admin' || currentRole?.toLowerCase() === 'super_admin') {
      return [
        { value: 'service', label: 'Service User' },
        { value: 'business', label: 'Business User' }
      ];
    } else if (currentRole?.toLowerCase() === 'service_admin') {
      // Service admin can create both service users (their own staff) and business users (for businesses they manage)
      return [
        { value: 'service', label: 'Service User' },
        { value: 'business', label: 'Business User' }
      ];
    } else if (currentRole?.toLowerCase() === 'operator_admin') {
      return [{ value: 'business', label: 'Business User' }];
    }
    return [];
  };

  // Determine available roles based on current user's role and selected user_type or target organization
  const getAvailableRoles = () => {
    const currentRole = currentUser?.organizations?.[0]?.role || currentUser?.role;

    // In assign mode, determine roles based on target organization type
    if (mode === 'assign' && selectedOrganizationId) {
      // Find the selected organization to check its type
      const selectedOrg = businesses.find(b => b.id === selectedOrganizationId);

      if (selectedOrg) {
        // If assigning to a business organization
        // Service admins can manage businesses, and businesses have their own operators
        if (selectedOrg.type === 'BUSINESS') {
          return [
            { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
            { value: ROLES.SERVICE_USER, label: 'Service User' },
            { value: ROLES.OPERATOR_ADMIN, label: 'Operator Admin' },
            { value: ROLES.OPERATOR_USER, label: 'Operator User' }
          ];
        }
        // If assigning to a service organization, only show service roles
        else if (selectedOrg.type === 'SERVICE') {
          if (currentRole?.toLowerCase() === 'master_admin') {
            return [
              { value: ROLES.MASTER_ADMIN, label: 'Master Admin' },
              { value: ROLES.SUPER_ADMIN, label: 'Super Admin' },
              { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
              { value: ROLES.SERVICE_USER, label: 'Service User' }
            ];
          } else if (currentRole?.toLowerCase() === 'super_admin') {
            return [
              { value: ROLES.SUPER_ADMIN, label: 'Super Admin' },
              { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
              { value: ROLES.SERVICE_USER, label: 'Service User' }
            ];
          }
          return [
            { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
            { value: ROLES.SERVICE_USER, label: 'Service User' }
          ];
        }
      }
    }

    // In create mode, use existing logic based on user type
    if (currentRole?.toLowerCase() === 'master_admin') {
      if (formData.user_type?.toLowerCase() === 'service') {
        return [
          { value: ROLES.MASTER_ADMIN, label: 'Master Admin' },
          { value: ROLES.SUPER_ADMIN, label: 'Super Admin' },
          { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
          { value: ROLES.SERVICE_USER, label: 'Service User' }
        ];
      } else {
        return [
          { value: ROLES.OPERATOR_ADMIN, label: 'Operator Admin' },
          { value: ROLES.OPERATOR_USER, label: 'Operator User' }
        ];
      }
    } else if (currentRole?.toLowerCase() === 'super_admin') {
      if (formData.user_type?.toLowerCase() === 'service') {
        return [
          { value: ROLES.SUPER_ADMIN, label: 'Super Admin' },
          { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
          { value: ROLES.SERVICE_USER, label: 'Service User' }
        ];
      } else {
        return [
          { value: ROLES.OPERATOR_ADMIN, label: 'Operator Admin' },
          { value: ROLES.OPERATOR_USER, label: 'Operator User' }
        ];
      }
    } else if (currentRole?.toLowerCase() === 'service_admin') {
      // Service admin can assign different roles based on user type
      if (formData.user_type?.toLowerCase() === 'business') {
        // For business users, service admin can assign service roles (to manage the business) and operator roles (business staff)
        return [
          { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
          { value: ROLES.SERVICE_USER, label: 'Service User' },
          { value: ROLES.OPERATOR_ADMIN, label: 'Operator Admin' },
          { value: ROLES.OPERATOR_USER, label: 'Operator User' }
        ];
      } else {
        // For service users (their own staff), only service roles
        return [
          { value: ROLES.SERVICE_ADMIN, label: 'Service Admin' },
          { value: ROLES.SERVICE_USER, label: 'Service User' }
        ];
      }
    } else if (currentRole?.toLowerCase() === 'operator_admin') {
      return [
        { value: ROLES.OPERATOR_ADMIN, label: 'Operator Admin' },
        { value: ROLES.OPERATOR_USER, label: 'Operator User' }
      ];
    }
    return [];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <h3 className="text-xl font-bold">
            {user ? 'Edit' : 'Add'} User
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
        {/* Mode Toggle - Only show when creating new user (not editing) */}
        {/* Hide "Assign User" mode for operator_admin - they should only create users for their business */}
        {!user && (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() !== 'operator_admin' && (
          <div className="p-6 border-b bg-gray-50 flex-shrink-0">
            <div className="flex gap-4">
              <button
                onClick={() => handleModeChange('create')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  mode === 'create'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
              >
                <UserPlus className="w-5 h-5" />
                <span className="font-medium">Create New User</span>
              </button>
              <button
                onClick={() => handleModeChange('assign')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  mode === 'assign'
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400 text-gray-700'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Assign Existing User</span>
              </button>
            </div>
          </div>
        )}

        {/* Search Section - Only show in assign mode */}
        {!user && mode === 'assign' && (
          <div className="p-6 border-b bg-blue-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search for existing user by username or email
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter username or email..."
              />
              <button
                onClick={handleSearchUser}
                disabled={searching}
                className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Search className="w-4 h-4" />
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
            {searchError && (
              <p className="text-sm text-red-600 mt-2">{searchError}</p>
            )}
            {searchedUser && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium">
                  ✓ User found: {searchedUser.username} ({searchedUser.email})
                </p>
                {searchedUser.organizations && searchedUser.organizations.length > 0 ? (
                  <div className="mt-2">
                    <p className="text-xs text-green-700 font-medium mb-1">Current Organizations:</p>
                    <ul className="text-xs text-green-600 ml-4 space-y-1">
                      {searchedUser.organizations.map((org, idx) => (
                        <li key={idx}>• {org.name} - {org.role}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-green-600 mt-2">
                      You can add this user to another organization below
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-green-600 mt-1">
                    This user is not assigned to any organization yet. Select organization and role below.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Assignment Details - Only show in assign mode */}
        {!user && mode === 'assign' && searchedUser && (
          <div className="p-6 border-b bg-yellow-50">
            <h4 className="text-md font-semibold text-gray-800 mb-4">
              {searchedUser.organizations && searchedUser.organizations.length > 0
                ? 'Add to Another Organization'
                : 'Assign to Organization'}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization *
                </label>
                {((currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'master_admin' ||
                  (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'super_admin' ||
                  (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'service_admin') ? (
                  <select
                    value={selectedOrganizationId}
                    onChange={(e) => setSelectedOrganizationId(e.target.value)}
                    disabled={loadingBusinesses}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      !selectedOrganizationId && errors.organization_id ? 'border-red-500' : 'border-gray-300'
                    } ${loadingBusinesses ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select Organization</option>
                    {businesses.map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.name} ({business.businessId})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={currentUser?.organizations?.[0]?.name || 'Your Organization'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                  />
                )}
                {!selectedOrganizationId && errors.organization_id && (
                  <p className="text-xs text-red-500 mt-1">Organization is required</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <select
                  value={formData.role || ''}
                  onChange={(e) => handleChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {getAvailableRoles().map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              {searchedUser.organizations && searchedUser.organizations.length > 0
                ? 'This user will be added to the selected organization with the specified role. They will retain their existing organization memberships.'
                : 'This user will be assigned to the selected organization with the specified role.'}
            </p>
          </div>
        )}

        <div className="p-6 space-y-4">
          {mode === 'assign' && searchedUser && (
            <div className="mb-4">
              <h4 className="text-md font-semibold text-gray-800 mb-2">User Information (Read-only)</h4>
              <p className="text-xs text-gray-600">Details of the existing user to be assigned</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username *
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleChange('username', e.target.value)}
                disabled={mode === 'assign' && !!searchedUser}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.username ? 'border-red-500' : 'border-gray-300'
                } ${mode === 'assign' && searchedUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="johndoe"
              />
              {errors.username && (
                <p className="text-xs text-red-500 mt-1">{errors.username}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                disabled={mode === 'assign' && !!searchedUser}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                } ${mode === 'assign' && searchedUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="John"
              />
              {errors.firstName && (
                <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                disabled={mode === 'assign' && !!searchedUser}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                } ${mode === 'assign' && searchedUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={mode === 'assign' && !!searchedUser}
                className={`w-full px-3 py-2 border rounded-lg ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                } ${mode === 'assign' && searchedUser ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Only show password field in create mode */}
          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password {user ? '(leave blank to keep current)' : '*'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password || ''}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={user ? 'Enter new password' : 'Enter password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4 text-gray-500" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs text-red-500 mt-1">{errors.password}</p>
              )}
            </div>
          )}

          {/* Only show user type and role fields in create mode */}
          {mode === 'create' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User Type *
                  </label>
                  <select
                    value={formData.user_type}
                    onChange={(e) => handleChange('user_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    disabled={(currentUser?.organizations?.[0]?.role || currentUser?.role) === 'operator_admin'}
                  >
                    {getAvailableUserTypes().map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {(currentUser?.organizations?.[0]?.role || currentUser?.role) === 'operator_admin' && (
                    <p className="text-xs text-gray-500 mt-1">Business users only</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  <select
                    value={formData.role || ''}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {getAvailableRoles().map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.user_type?.toLowerCase() === 'business' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Organization *
                  </label>
                  {((currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'master_admin' ||
                    (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'super_admin' ||
                    (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'service_admin') ? (
                    <select
                      value={formData.organization_id || ''}
                      onChange={(e) => handleChange('organization_id', e.target.value)}
                      disabled={loadingBusinesses}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        errors.organization_id ? 'border-red-500' : 'border-gray-300'
                      } ${loadingBusinesses ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Select Business</option>
                      {businesses.filter(org => org.type === 'BUSINESS').map((business) => (
                        <option key={business.id} value={business.id}>
                          {business.name} ({business.businessId})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={currentUser?.organizations?.[0]?.name || ''}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                      disabled
                    />
                  )}
                  {errors.organization_id && (
                    <p className="text-xs text-red-500 mt-1">{errors.organization_id}</p>
                  )}
                  {(currentUser?.organizations?.[0]?.role || currentUser?.role) === 'operator_admin' && (
                    <p className="text-xs text-gray-500 mt-1">Users will be created in your organization</p>
                  )}
                </div>
              )}

              {formData.user_type?.toLowerCase() === 'service' && ((currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'super_admin' || (currentUser?.organizations?.[0]?.role || currentUser?.role)?.toLowerCase() === 'master_admin') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization * {(formData.role === 'master_admin' || formData.role === 'super_admin') ? (
                      <span className="text-xs text-gray-500">(Service Provider Only)</span>
                    ) : (
                      <span className="text-xs text-gray-500">(Service or Business)</span>
                    )}
                  </label>
                  <select
                    value={formData.organization_id || ''}
                    onChange={(e) => handleChange('organization_id', e.target.value)}
                    disabled={loadingBusinesses}
                    className={`w-full px-3 py-2 border rounded-lg ${
                      errors.organization_id ? 'border-red-500' : 'border-gray-300'
                    } ${loadingBusinesses ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select Organization</option>
                    {businesses
                      .filter(org => {
                        // If MASTER_ADMIN or SUPER_ADMIN role is selected, only show SERVICE type organizations
                        if (formData.role === 'master_admin' || formData.role === 'super_admin') {
                          return org.type === 'SERVICE';
                        }
                        // For other roles, show all organizations
                        return true;
                      })
                      .map((org) => (
                        <option key={org.id} value={org.id}>
                          {org.name} ({org.type === 'SERVICE' ? 'Service Provider' : 'Business'}) {org.businessId ? `- ${org.businessId}` : ''}
                        </option>
                      ))}
                  </select>
                  {errors.organization_id && (
                    <p className="text-xs text-red-500 mt-1">{errors.organization_id}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {(formData.role === 'master_admin' || formData.role === 'super_admin')
                      ? 'MASTER_ADMIN and SUPER_ADMIN can only be assigned to service provider organizations'
                      : 'Service users can be assigned to service organizations (as staff) or business organizations (to manage them)'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="tl-btn tl-btn-primary"
          >
            <Save className="w-4 h-4" />
            {mode === 'assign'
              ? (searchedUser?.organizations && searchedUser.organizations.length > 0
                  ? 'Add to Organization'
                  : 'Assign to Organization')
              : 'Save User'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;